import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";
import { ApiFactory } from "../../api";
import { Identifier } from "../../models";
import { DossierMetaData, DossierType } from "../../models/dossier-metadata";
import { ReducerErrorToaster } from "../../utils/ReducerUtils";
import { InsertOrRemoveIdentifier, removeEmpty } from "../../utils/StateHelpers";
import { setToast } from "./toast";

export interface MetadataState {
  data: DossierMetaData;
  uploadDossier: boolean;
  fetching: boolean;
  formUniqueKey: number;
  documentError: {
    pid?: string[];
    indexList?: number[];
  };
  errorReviewed: boolean;
  localValidationError: boolean;
  mode: "create" | "update";
  hasChangedMeta?: boolean;
}

const initialState: MetadataState = {
  data: {
    pid: undefined,
    titel: undefined,
    type: undefined,
    identificatiekenmerken: [],
    dossierHouder: undefined,
  },
  fetching: false,
  formUniqueKey: 0,
  documentError: {},
  errorReviewed: false,
  localValidationError: false,
  hasChangedMeta: false,
  mode: "create",
  uploadDossier: false,
};

const validateForm = (data: DossierMetaData): string | undefined => {
  if (!validateRequiredFields(data)) {
    return "Verplichte velden niet ingevuld.";
  }

  if (!validateFormTextInputs(data)) return "Minimaal 3 karakters vereist.";

  if (!validateFormTextInputsMaxLength(data))
    return "Maximaal aantal karakters overschreden voor 1 of meerdere velden.";
};

const validateRequiredFields = (data: DossierMetaData): boolean => {
  let requiredValues: (undefined | string | Identifier[])[] = [
    data.titel,
    data.dossierHouder?.id,
    data.type,
    data.identificatiekenmerken[0],
  ];

  return requiredValues.every((rv: undefined | string | Identifier[]) => rv && rv.length > 0);
};

const validateFormTextInputs = (data: DossierMetaData): boolean => {
  type tekstType = string | string[] | undefined;
  const minChar: number = 3;
  const { titel, identificatiekenmerken, omschrijving, creatieDatum } = data;
  let textValuesRequired = [titel, identificatiekenmerken];
  let textValuesOptional = [omschrijving, creatieDatum];

  const textValuesRequiredIncorrect = textValuesRequired.filter(
    (tekst: tekstType) =>
      tekst !== undefined &&
      tekst.length > 0 &&
      !(Array.isArray(tekst) ? tekst.every((arrayValue) => arrayValue.length >= minChar) : tekst.length >= minChar)
  );
  const textValuesOptionalIncorrect = textValuesOptional.filter(
    (tekst: tekstType) =>
      tekst !== undefined &&
      !(Array.isArray(tekst)
        ? tekst.every((arrayValue) => arrayValue.length >= minChar || arrayValue.length === 0)
        : tekst.length >= minChar || tekst.length === 0)
  );
  return textValuesRequiredIncorrect.length === 0 && textValuesOptionalIncorrect.length === 0;
};

const validateFormTextInputsMaxLength = (data: DossierMetaData): boolean => {
  const max200: number = 200;
  const max1000: number = 1000;
  const { titel, identificatiekenmerken, omschrijving, creatieDatum } = data;
  const validatie200 = [titel, identificatiekenmerken, creatieDatum];
  const validatie1000 = [omschrijving];

  const validatie200Incorrect = validatie200.filter(
    (tekst: string | string[] | undefined) =>
      tekst !== undefined &&
      !(Array.isArray(tekst)
        ? tekst.every((arrayValue) => arrayValue.length <= max200 || arrayValue.length === 0)
        : tekst.length <= max200 || tekst.length === 0)
  );

  const validatie1000Incorrect = validatie1000.filter(
    (tekst: string | string[] | undefined) =>
      tekst !== undefined &&
      !(Array.isArray(tekst)
        ? tekst.every((arrayValue) => arrayValue.length <= max1000 || arrayValue.length === 0)
        : tekst.length <= max1000 || tekst.length === 0)
  );

  return validatie200Incorrect.length === 0 && validatie1000Incorrect.length === 0;
};

export const validate = createAsyncThunk<void, void, { state: RootState }>("dossier/validate", async (_, thunkAPI) => {
  const state = thunkAPI.getState().dossier;
  const errorMsg: string | undefined = validateForm(state.data);

  if (errorMsg) {
    const error = new Error(errorMsg);
    ReducerErrorToaster(thunkAPI, error);
    thunkAPI.dispatch(updateLocalValidationError(true));
    throw error;
  }

  thunkAPI.dispatch(updateLocalValidationError(false));
});

export const create = createAsyncThunk<unknown, undefined, { state: RootState }>(
  "dossier/create",
  async (_, thunkAPI) => {
    const metadata_api = ApiFactory.createDossierMetadataApi();

    let state: MetadataState = thunkAPI.getState().dossier;
    const errorMsg: string | undefined = validateForm(state.data);
    if (errorMsg) {
      const error = new Error(`Validation failed: ${errorMsg}`);
      ReducerErrorToaster(thunkAPI, error);
      thunkAPI.dispatch(updateLocalValidationError(true));
      throw error;
    }

    thunkAPI.dispatch(updateLocalValidationError(false));

    await metadata_api.createMetadataSession(state.data).catch((error: any) => {
      ReducerErrorToaster(thunkAPI, error);
      throw error;
    });

    thunkAPI.dispatch(
      setToast({
        autoClose: true,
        type: "success",
        message: {
          message: "Dossier aangeleverd.",
        },
      })
    );

    thunkAPI.dispatch(clearDossierState());
  }
);

///BE is er nog niet moet dus nog geimplementeerd worden
export const unpublish = createAsyncThunk(
  "dossier/delete",
  async (
    args: {
      id: string;
    },
    thunkAPI
  ) => {
    if (!args.id) {
      const error = new Error("Id niet gevonden.");
      ReducerErrorToaster(thunkAPI, error);
      throw error;
    }

    thunkAPI.dispatch(
      setToast({
        autoClose: true,
        type: "success",
        message: {
          message: "Dossier is gedepubliceerd",
        },
      })
    );

    return args.id;
  }
);

const dossierState = createSlice({
  name: "dossier",
  initialState,
  reducers: {
    updateLocalValidationError: (state, action: PayloadAction<boolean>) => {
      state.localValidationError = action.payload;
    },
    clearDossierState: (state) => {
      state.formUniqueKey += 1;
      state.data = {
        pid: undefined,
        titel: undefined,
        type: undefined,
        identificatiekenmerken: [],
        dossierHouder: undefined,
        creatieDatum: undefined,
        omschrijving: undefined,
        themas: undefined,
        relaties: undefined,
      };
      state.documentError = {};
      state.fetching = false;
      state.errorReviewed = false;
      state.localValidationError = false;
      state.mode = "create";
    },
    updatePublisher: (state, action: PayloadAction<Identifier>) => {
      state.data.dossierHouder = action.payload;
      state.hasChangedMeta = true;
    },
    updateDossierType: (state, action: PayloadAction<Identifier>) => {
      state.data.type = action.payload.id as DossierType;
      state.hasChangedMeta = true;
    },
    updateTitel: (state, action: PayloadAction<string>) => {
      state.data.titel = action.payload;
      state.hasChangedMeta = true;
    },
    updateCreatieDatum: (state, action: PayloadAction<string>) => {
      state.data.creatieDatum = removeEmpty(action.payload);
      state.hasChangedMeta = true;
    },
    updateIdentifiers: (state, action: PayloadAction<string[]>) => {
      state.data.identificatiekenmerken = action.payload;
      state.hasChangedMeta = true;
    },
    updateThemas: (state, action: PayloadAction<Identifier>) => {
      state.data.themas = InsertOrRemoveIdentifier(state.data.themas, action.payload);
      state.hasChangedMeta = true;
    },
    updateOmschrijving: (state, action: PayloadAction<string>) => {
      state.data.omschrijving = action.payload;
      state.hasChangedMeta = true;
    },
    uploadDossier: (state, action: PayloadAction<boolean>) => {
      state.uploadDossier = action.payload;
    },
    extraReducers: (caseBuilder) => {},
  },
});

const { updateLocalValidationError } = dossierState.actions;
export const {
  updateThemas,
  updateCreatieDatum,
  updateIdentifiers,
  updateTitel,
  updatePublisher,
  updateDossierType,
  uploadDossier,
  updateOmschrijving,
  clearDossierState,
} = dossierState.actions;
export default dossierState.reducer;
